import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogTitle, Paper, FormControl, ThemeProvider, createMuiTheme, Button, makeStyles, createStyles, Theme } from '@material-ui/core';
import Graphic from './Graphic';
import { FormContext, useForm } from 'react-hook-form';
import ILocationDetailsDto, { IUpdateLocationDto } from '../../../API/Interfaces/ILocationDetailsDto';
import FileServiceAPI from '../../../API/Services/FileServiceAPI';
import { DictImageType } from '../../../API/Interfaces/Enums';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { debounce } from '../../../Constants/constants';
import Helper from '../../../Utils/Helpers';
import LocationsServiceAPI from '../../../API/Services/LocationServiceAPI';

interface ILocationGraphicsProps {
	entity: ILocationDetailsDto;
	onRefresh: () => void;
}

const blueMuiTheme = createMuiTheme({ palette: { primary: { main: '#1045FF' } } });

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		tile: {
			marginRight: '36px',
		},
		paper: { boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.25)', paddingTop: '20px' },
	})
);

export const LocationGraphics = (props: ILocationGraphicsProps) => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { enqueueSnackbar } = useSnackbar();
	const [logos, setLogos] = useState();
	const [sidePictures, setSidePictures] = useState();
	const [aboutPictures, setAboutPictures] = useState();
	const [mainPictures, setMainPictures] = useState();
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });
	const savedDialog = () => enqueueSnackbar(t('common.changesSaved'), { variant: 'success' });
	const classes = useStyles();

	const methods = useForm({
		defaultValues: props.entity,
		validateCriteriaMode: 'all',
		submitFocusError: true,
		mode: 'onBlur',
		reValidateMode: 'onChange',
	});
	const { handleSubmit, errors } = methods;

	const fetchLogos = async function () {
		try {
			const response = await FileServiceAPI.GetImages({ locationId: id, imageType: DictImageType.Logo });
			if (response.success) return setLogos(response.data.items);
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	const fetchSidePictures = async function () {
		try {
			const response = await FileServiceAPI.GetImages({ locationId: id, imageType: DictImageType.SidePicture });
			if (response.success) return setSidePictures(response.data.items);
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	const fetchAboutPictures = async function () {
		try {
			const response = await FileServiceAPI.GetImages({ locationId: id, imageType: DictImageType.AboutPicture });
			if (response.success) return setAboutPictures(response.data.items);
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	const fetchMainPictures = async function () {
		try {
			const response = await FileServiceAPI.GetImages({ locationId: id, imageType: DictImageType.MainPicture });
			if (response.success) return setMainPictures(response.data.items);
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	useEffect(() => {
		fetchLogos();
		fetchSidePictures();
		fetchAboutPictures();
		fetchMainPictures();

		// eslint-disable-next-line
	}, []);

	const update = async (data: any) => {
		Helper.setLoader(true);
		const request: IUpdateLocationDto = {
			locationId: props.entity.locationId,
			CPlocationId: props.entity.CPlocationId,
			locationName: props.entity.locationName,
			locationCode: props.entity.locationCode,
			street: props.entity.street,
			zipCode: props.entity.zipCode,
			city: props.entity.city,
			logoId: data.logoId,
			mainPictureId: data.mainPictureId !== '' ? data.mainPictureId : undefined,
			aboutPictureId: data.aboutPictureId !== '' ? data.aboutPictureId : undefined,
			sidePictureId: data.sidePictureId,
		};
		const response = await LocationsServiceAPI.Update(request);

		if (response !== null) {
			Helper.setLoader(false);
			if (response === 1) {
				savedDialog();
				props.onRefresh();
			} else {
				errorDialog(t(`errorCodes.${response}`));
			}
		} else {
			errorDialog(t('errorCodes.0'));
		}
	};

	const onSubmit = (data: any) => {
		if (errors && Object.keys(errors).length === 0 && Object.getPrototypeOf(errors) === Object.prototype) {
			update(data);
		} else return;
	};

	return (
		<>
			<Paper className={classes.paper}>
				<DialogTitle>{t('location.tabs.graphics')}</DialogTitle>
				<div style={{ padding: '30px 60px' }}>
					<FormContext {...methods}>
						<FormControl>
							<div style={{ display: 'flex', flexWrap: 'wrap' }}>
								<div className={classes.tile}>
									<Graphic title={t('repository.logo')} name='logoId' options={logos ?? []} isRequired />
								</div>
								<div className={classes.tile}>
									<Graphic title={t('repository.mainTab')} name='mainPictureId' options={mainPictures ?? []} />
								</div>
								<div className={classes.tile}>
									<Graphic title={t('repository.aboutTab')} name='aboutPictureId' options={aboutPictures ?? []} />
								</div>
								<Graphic
									title={t('repository.sidePicture')}
									name='sidePictureId'
									options={sidePictures ?? []}
									isRequired
								/>
							</div>
						</FormControl>
					</FormContext>

					<ThemeProvider theme={blueMuiTheme}>
						<Button
							style={{ marginLeft: 'auto', display: 'block', marginTop: '10px' }}
							onClick={debounce(handleSubmit(onSubmit), 5000, true)}
							color='primary'
							variant='contained'
						>
							{t('common.save')}
						</Button>
					</ThemeProvider>
				</div>
			</Paper>
		</>
	);
};
